import React from "react";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import TrustPageIntro from "../components/TrustPageIntro";
import Typography from "../components/ui/Typography";
import usePageMeta from "../hooks/usePageMeta";
import { handleInternalLinkClick } from "../utils/navigation";
import { buildStaticPageSeoMeta } from "./static/staticPageSeo";
import { COVERAGE_COUNTRIES } from "../utils/coverage";

const promises = [
  {
    title: "A reason for every pick",
    copy: "Each recommendation explains how it fits a show you like: its humour, tension, pace or characters. Shows-like pages include the closest picks and, where available, deeper cuts.",
  },
  {
    title: "Scripted shows across platforms",
    copy: `Browse new and returning TV from streaming services and broadcasters in ${COVERAGE_COUNTRIES}. Reality, sport, news and other unscripted formats are left out.`,
  },
  {
    title: "Descriptions grounded in listings",
    copy: "Show descriptions reflect published listings from Watchmode, TVMaze, OMDb and TMDB, with additional metadata from TheTVDB. Conflicting descriptions can be held for review. The sources can still be wrong.",
  },
];
const AboutPage = () => {
  usePageMeta(buildStaticPageSeoMeta("about"));
  return (
    <div className="min-h-screen bg-background text-foreground">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "About PremiereLens",
            url: "https://premierelens.com/about",
            description:
              "New and returning TV worth your time, and what to watch after the show you just finished.",
            isPartOf: {
              "@type": "WebSite",
              name: "PremiereLens",
              url: "https://premierelens.com",
            },
          }),
        }}
      />
      <SubpageHeader />
      <main id="main-content" tabIndex={-1} className="route-shell route-shell--editorial">
        <div className="mx-auto max-w-5xl space-y-10">
          <TrustPageIntro
            title="About PremiereLens"
            description="New and returning TV worth your time, and what to watch after the show you just finished."
            actions={
              <a
                className="site-browse-link text-primary underline underline-offset-4"
                href="/"
                onClick={(event) => handleInternalLinkClick(event, "/")}
              >
                Browse upcoming premieres
              </a>
            }
          />
          <section
            aria-labelledby="about-promises"
            className="grid gap-6 md:grid-cols-[1fr_2fr]"
          >
            <Typography as="h2" id="about-promises" variant="h2">
              How it helps
            </Typography>
            <div className="space-y-6">
              {promises.map(({ title, copy }) => (
                <article key={title} className="border-b border-border pb-6">
                  <Typography as="h3" variant="h3">
                    {title}
                  </Typography>
                  <p className="reading-measure mt-3 text-body leading-relaxed text-muted-foreground">
                    {copy}
                  </p>
                </article>
              ))}
            </div>
          </section>
          <section
            aria-labelledby="about-selection"
            className="reading-measure space-y-4"
          >
            <Typography as="h2" id="about-selection" variant="h2">
              What makes the list
            </Typography>
            <p className="text-body leading-relaxed">
              Returning shows need at least 7.0 on IMDb, or the listing
              service’s viewer rating when IMDb has none. New series often have
              no ratings yet; their network, genre and description help identify
              them as scripted shows.
            </p>
            <p className="text-body leading-relaxed">
              The guide leaves out reality TV, game shows, talk shows, news,
              sport, children’s programming, soap operas and documentary series.
              Release coverage depends on the source listings and can vary by
              country.
            </p>
          </section>
          <section
            aria-labelledby="about-archive"
            className="reading-measure space-y-4"
          >
            <Typography as="h2" id="about-archive" variant="h2">
              After premiere week
            </Typography>
            <p className="text-body leading-relaxed">
              Completed months stay in the archive. Search by a mood, setup or
              title, or browse each month’s highest-rated premieres. My List
              keeps saved shows in your browser on this device; it does not need
              an account.
            </p>
            <nav
              aria-label="Explore PremiereLens"
              className="flex flex-wrap gap-2"
            >
              {[
                ["/archive", "Search the archive"],
                ["/most-recommended", "Recommendations"],
                ["/faq", "Frequently asked questions"],
              ].map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  className="site-browse-link text-primary underline underline-offset-4"
                  onClick={(event) => handleInternalLinkClick(event, href)}
                >
                  {label}
                </a>
              ))}
            </nav>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};
export default AboutPage;
